<template>
  <div class="telegram-chats">
    <div class="search-bar">
      <input 
        type="text" 
        v-model="searchQuery" 
        placeholder="Поиск" 
        class="search-input"
      />
    </div>
    
    <div class="chats-list">
      <!-- Закрепленные чаты -->
      <div v-if="pinnedChats.length > 0" class="pinned-chats">
        <div class="section-title">Закрепленные</div>
        <div 
          v-for="chat in pinnedChats" 
          :key="chat._id" 
          class="chat-item" 
          :class="{ 'active': selectedChatId === chat._id }"
          @click="selectChat(chat._id)"
        >
          <ChatItem :chat="chat" />
        </div>
      </div>
      
      <!-- Обычные чаты -->
      <div class="regular-chats">
        <div class="section-title" v-if="pinnedChats.length > 0">Все чаты</div>
        <div 
          v-for="chat in regularChats" 
          :key="chat._id" 
          class="chat-item" 
          :class="{ 'active': selectedChatId === chat._id }"
          @click="selectChat(chat._id)"
        >
          <ChatItem :chat="chat" />
        </div>
      </div>
      
      <div v-if="filteredChats.length === 0" class="no-chats">
        {{ searchQuery ? 'Чаты не найдены' : 'Нет чатов' }}
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import axios from 'axios';
import socket, { requestChats } from '../plugins/socket';
import ChatItem from './ChatItem.vue';

export default {
  name: 'TelegramChats',
  components: {
    ChatItem
  },
  props: {
    selectedChatId: {
      type: String,
      default: null
    }
  },
  setup(props, { emit }) {
    const store = useStore();
    const router = useRouter();
    const searchQuery = ref('');
    let updateInterval = null;
    const loading = ref(false);
    const lastUpdateTime = ref(0);
    
    // Получаем чаты из хранилища
    const chats = computed(() => store.getters.getChats);
    
    // Фильтруем чаты по поисковому запросу
    const filteredChats = computed(() => {
      if (!searchQuery.value) return chats.value;
      
      const query = searchQuery.value.toLowerCase();
      return chats.value.filter(chat => {
        const name = `${chat.firstName} ${chat.lastName}`.trim().toLowerCase();
        const username = (chat.username || '').toLowerCase();
        const lastMessage = (chat.lastMessage && chat.lastMessage.text) ? 
          chat.lastMessage.text.toLowerCase() : '';
        
        return name.includes(query) || 
               username.includes(query) || 
               lastMessage.includes(query);
      });
    });
    
    // Разделяем чаты на закрепленные и обычные
    const pinnedChats = computed(() => {
      return filteredChats.value.filter(chat => chat.isPinned);
    });
    
    const regularChats = computed(() => {
      return filteredChats.value.filter(chat => !chat.isPinned);
    });
    
    // Выбор чата
    const selectChat = (chatId) => {
      router.push(`/chat/${chatId}`);
    };
    
    // Запрос на обновление списка чатов с защитой от частых запросов
    const requestUpdate = () => {
      const now = Date.now();
      // Ограничиваем частоту запросов до одного в 5 секунд
      if (now - lastUpdateTime.value < 5000) {
        console.log('TelegramChats: Запрос на обновление слишком частый, игнорируем');
        return;
      }
      
      lastUpdateTime.value = now;
      console.log('TelegramChats: Запрос на обновление списка чатов');
      requestChats();
    };
    
    // Получение списка чатов с сервера
    const fetchChats = async () => {
      if (loading.value) return;
      
      loading.value = true;
      try {
        console.log('Запрос списка чатов с сервера');
        const response = await axios.get('/telegram/chats');
        console.log(`Получено ${response.data.length} чатов с сервера`);
        store.commit('setChats', response.data);
      } catch (error) {
        console.error('Ошибка при получении чатов:', error);
      } finally {
        loading.value = false;
      }
    };
    
    // Инициализация компонента
    onMounted(() => {
      console.log('TelegramChats: компонент смонтирован');
      
      // Запрашиваем список чатов при монтировании
      requestChats();
      
      // Подписываемся на обновления списка чатов
      socket.on('chats_list', handleChatsList);
      
      // Подписываемся на обновления отдельных чатов
      socket.on('chat_updated', (updatedChat) => {
        if (updatedChat && updatedChat._id) {
          console.log(`TelegramChats: Получено обновление чата ${updatedChat._id}`);
          store.dispatch('updateChatSilently', updatedChat);
        } else {
          console.error('TelegramChats: Получен некорректный объект чата:', updatedChat);
        }
      });
    });
    
    // Обработчик получения списка чатов
    const handleChatsList = (chats) => {
      console.log(`TelegramChats: Получен список чатов (${chats.length} чатов)`);
      store.commit('setChats', chats);
    };
    
    // Очистка при размонтировании компонента
    onUnmounted(() => {
      console.log('TelegramChats: компонент размонтирован');
      
      // Отписываемся от обновлений списка чатов
      socket.off('chats_list', handleChatsList);
      
      // Отписываемся от обновлений отдельных чатов
      socket.off('chat_updated');
    });
    
    return {
      searchQuery,
      filteredChats,
      pinnedChats,
      regularChats,
      selectChat,
      requestUpdate,
      fetchChats
    };
  }
};
</script>

<style scoped>
.telegram-chats {
  display: flex;
  flex-direction: column;
  height: 100%;
  border-right: 1px solid #e0e0e0;
  background-color: #ffffff;
  max-width: 350px;
  width: 100%;
}

.search-bar {
  padding: 10px;
  border-bottom: 1px solid #e0e0e0;
}

.search-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  font-size: 14px;
  outline: none;
}

.search-input:focus {
  border-color: #2196f3;
}

.chats-list {
  flex: 1;
  overflow-y: auto;
  padding: 0;
}

.section-title {
  padding: 8px 15px;
  font-size: 12px;
  color: #707579;
  background-color: #f5f5f5;
}

.chat-item {
  cursor: pointer;
  transition: background-color 0.2s;
  max-width: 100%;
  overflow: hidden;
}

.chat-item:hover {
  background-color: #f0f0f0;
}

.chat-item.active {
  background-color: #e6f2fd;
}

.no-chats {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
  color: #707579;
  font-size: 14px;
}
</style> 